import _ from 'lodash'
import $ from './helper'
import Helper from '@utils/helper'
import { REGEX_IS_TEXT } from '@src/plugins/vue-gridform/shared/constants'
import CONSTANTS from '@src/constants'

const _PICK_FIELDLIST_PROPS = ['id', 'initials', 'name', 'rating', 'weighting']
const _buildInitials = fl => {
    return fl.initials && fl.initials.charAt(0) !== '_' ? fl.initials : fl.name.toUpperCase()
}

export default class Rating {
    _fls = [] // unfiltered fieldlists
    fls = [] // only fieldlists with valid rating
    calced = {}

    constructor() {}

    add(fl) {
        const tmpfls = _.isArray(fl) ? fl : [fl]
        _.each(tmpfls, fl => {
            const {fieldlist, rows} = fl
            this.fls.push({..._.pick(fieldlist, _PICK_FIELDLIST_PROPS), rows})
        })
        this.recalc()
    }

    recalc() {
        const datasetPercent = []
        const labels = []
        let result = 0
        let resultWeighted = 0
        let total = 0
        let totalWeighted = 0
        // only fieldlists with rating object
        _.filter(this.fls, fl => fl.rating).forEach(fl => {
            const percent = _.round(fl.rating.percent, 1)
            // const percent = parseFloat(fl.rating.percent.toFixed(1))
            datasetPercent.push(percent)
            labels.push(_buildInitials(fl))
            result += fl.rating.score ?? 0
            resultWeighted += fl.rating.wscore ?? 0
            total += fl.rating.max
            totalWeighted += fl.rating.wmax
        })
        const percent = total ? result * 100 / total : 0
        const percentWeighted = totalWeighted ? resultWeighted * 100 / totalWeighted : 0
        this.calced = {
            result,
            resultWeighted,
            total,
            totalWeighted,
            percent,
            percentWeighted,
            datasetPercent,
            color: $.percentToColor(percent),
            colorWeighted: $.percentToColor(percentWeighted),
            labels
        }
    }

    isWeighted() {
        return this.result() !== this.resultWeighted()
    }

    result() {
        return this.calced.result
    }
    resultWeighted() {
        return this.calced.resultWeighted
    }
    total() {
        return this.calced.total
    }
    totalWeighted() {
        return this.calced.totalWeighted
    }
    percent() {
        return this.calced.percent
    }
    percentWeighted() {
        return this.calced.percentWeighted
    }
    datasetPercent() {
        return this.calced.datasetPercent
    }
    color() {
        return this.calced.color
    }
    colorWeighted() {
        return this.calced.colorWeighted
    }
    labels() {
        return this.calced.labels
    }
    summaryGroups() {
        return _.map(this.fls, fl => {
            const rating = fl.rating || {}
            const name = _buildInitials(fl)
            return {
                key: _.uniqueId(name),
                name,
                grade: $.percentToGrade(rating.percent),
                rating: `${rating.wscore} / ${rating.wmax} P`,
                wscore: rating.wscore,
                wmax: rating.wmax,
                percent: rating.percent,
                diff: rating.diff
            }
        })
    }
    summaryTotal() {
        const {result, resultWeighted, total, totalWeighted, percent} = this.calced
        const rating = `${resultWeighted} / ${totalWeighted} P`
        const diff = result - resultWeighted
        return {result, resultWeighted, total, totalWeighted, rating, grade: $.percentToGrade(percent), percent, diff}
    }

    groupByIds() {
        const groups = {}
        _.each(this.fls, fl => {
            const weighting = parseInt(fl.weighting) || 100
            let diff = 0
            const rows = _.flatten(fl.rows).map(field => {
                const { name, label, hint, tickLabels, type, value, rating } = field
                // calculate weighting from group to field
                if (rating && weighting > 0 && weighting <= 100) {
                    rating.wscore = rating.wscore * weighting / 100
                    rating.wmax = rating.wmax * weighting / 100
                    rating.diff = rating.max - rating.wmax
                    diff += rating.diff
                }
                return {
                    name,
                    label,
                    hint,
                    tickLabels,
                    value,
                    rating,
                    _type: type,
                    type: Helper.translateTemplateType(type),
                    data: this.extractFieldData(field)
                }
            })
            if (fl.rating) fl.rating.diff = diff
            groups[fl.id] = {
                initials: fl.initials,
                name: fl.name,
                rating: fl.rating,
                rows,
                weighting
            }
        })
        return groups
    }

    extractFieldData(field) {
        let data
        if (REGEX_IS_TEXT.test(field.type)) {
            data = []
            if (_.isString(field.value)) {
                if (field.value) data.push(field.value)
            }
            else if (_.isArray(field.value)) {
                _.each(field.value, v => {data.push(_.get(v, 'label', v))})
                data = _.sortBy(data)
            }
            else if (_.isObject(field.value)) data.push(_.get(field.value, 'label'))
        }
        else if (field.type === 'radio') {
            const selected = _.find(field.items, item => item.value === field.value)
            if (selected) {
                data = []
                data.push(selected.label)
            }
        }
        else if (field.type === 'rating') {
            data = [field.value]
        }
        return data
    }

    static fromFieldlists(fls) {
        const R = new Rating()
        R._fls = _.cloneDeep(fls)
        // only fieldlists that are not excluded from rating
        // set default fieldlist properties to fieldlist for grouping issues
        const rfls = fls.filter(fl => !_.get(fl.fieldlist, 'noRating')).map(fl => {
            return _.merge({}, {fieldlist: CONSTANTS.DEFAULT_FIELDLIST}, fl)
        })
        R.add(rfls)
        return R
    }
}

export class RatingField {
    calced
    field

    constructor(_field) {
        const field = this.field = _.cloneDeep(_field)
        const fieldlist = field.fieldlist || {}
        if (fieldlist.internal) return // skip internal fieldlists
        if (fieldlist.activate && !fieldlist.activated) return // skip deactivated fieldlists

        const rating = field.rating || {}
        const noRating = !!rating.noRating // is field excluded from rating?
        const weighting = field.weighting || 100

        let value = field.value
        let score
        let min
        let max
        let maxValue

        if (field.type === 'slider') {
            value = Number(field.value)
            const tickLabels = field.customLabels || field.tickLabels
            const tl = Array.isArray(tickLabels) ? tickLabels : typeof tickLabels === 'string' ? tickLabels.split(',') : []
            min = Number(tl.length ? tl[0] : field.min)
            max = Number(tl.length ? _.last(tl) : field.max)
            // score = !_.isUndefined(field.value) && tl.length ? Number(tl[field.value]) : field.value || min
            // prevent invalid value
            if (value < min) field.value = min
            if (value > max) field.value = max
            // current value is current score
            score = Number(field.value)

            // set component props if not defined
            field.min = min
            field.max = max
            // field.min = field.min || min
            // field.max = field.max || tl.length - 1
            field.tickLabels = !_.isEmpty(tl) ? tl : _.range(field.min, field.max + 1, field.step)
            field.tickLabels = field.tickLabels.map((v) => v.toString())
            // maxValue = parseInt(_.last(field.tickLabels)) || field.tickLabels.length - 1
            maxValue = field.max
        }
        else if (field.type === 'checkbox' || field.type === 'switch') {
            min = 0
            max = 1
            score = field.value ? 1 : 0 // field is true or false
            maxValue = true
        }
        else if (field.type === 'rating') {
            min = 0
            max = 5
            score = field.value
            maxValue = max
        }
        else if (field.type === 'range') {
            min = field.min
            max = field.max
            maxValue = max - min + 1
            if (Array.isArray(field.value)) {
                // fix invalid range value
                if (field.value[0] > max) field.value[0] = min
                if (field.value[1] > max) field.value[1] = max
                score = (field.value[1] - field.value[0]) + 1
            }
            const tickLabels = field.customLabels || field.tickLabels
            field.tickLabels = !_.isEmpty(tickLabels) ? tickLabels : _.range(field.min, field.max + 1, field.step)
        }
        else if (field.type === 'combobox' || field.type === 'radio') {
            maxValue = 'items'
            min = !field.multiple ? field.min || 0 : 0
            max = !field.multiple ? field.max || 0 : 0

            if (Array.isArray(field.items)) {
                if (_.isString(min)) {
                    const c_min = _.minBy(field.items, item => Number(item.avatar))
                    if (c_min) min = Number(c_min.avatar)
                }
                if (_.isString(max)) {
                    const c_max = _.maxBy(field.items, item => Number(item.avatar))
                    if (c_max) max = Number(c_max.avatar)
                }
                let val
                field.items.forEach(item => {
                    item.avatar = typeof item.avatar !== 'undefined' ? `${item.avatar}` : '1'
                    val = Number(item.avatar)
                    // multiple selections => possible ratings must be added
                    if (field.multiple) val > 0 ? max += val : min += val
                    else {
                        if (_.isNumber(min)) min = Math.min(min, val)
                        if (_.isNumber(max)) max = Math.max(max, val)
                    }
                })
            }

            score = 0
            if (Array.isArray(field.value)) {
                if (field.type === 'radio') {
                    field.items.forEach(v => {
                        if (field.value.includes(v.value)) score += Number(v.avatar)
                    })
                }
                else field.value.forEach(v => {score += Number(v.avatar)})
            }
            else {
                // TODO check this, when field.value === "" (ex. type=radio) score is set to 1 instead of 0
                const item = _.isObject(field.value)
                    ? field.value
                    : _.find(field.items, item => item.value === field.value)
                score = item ? Number(item.avatar) : 0
                if (!item) {
                    const c_score = _.get(field, 'rating.score')
                    const c_item = _.find(field.items, item => item.avatar === `${c_score}`)
                    if (c_item) {
                        score = c_score
                        field.value = c_item.value
                        // const c_min = _.minBy(field.items, item => Number(item.avatar))
                        // const c_max = _.maxBy(field.items, item => Number(item.avatar))
                        // console.log('c_min, c_max', c_min, c_max)
                        min = field.rating.min
                        max = field.rating.max
                    }
                }
            }
        }
        else if (field.rating) {
            min = field.rating.min
            max = field.rating.max
            score = field.rating.score
            // NOTE we could not use this as value, gf.rating() checks this and update the rating object
            maxValue = 'rating'
        }
        if (typeof score === 'undefined') return

        this.calced = { min, max, maxValue, score, noRating, weighting }
        this.recalc()
    }

    recalc() {
        if (_.isUndefined(this.calced.max)) return

        if (this.calced.score < this.calced.min) this.calced.score = this.calced.min
        if (this.calced.max > this.calced.maxValue) this.calced.max = this.calced.maxValue

        this.calced.wscore = this.calced.score
        this.calced.wmax = this.calced.max
        if (this.calced.weighting >= 0 && this.calced.weighting < 100) {
            this.calced.wscore = this.calced.score * this.calced.weighting / 100
            this.calced.wmax = this.calced.max * this.calced.weighting / 100
        }

        this.calced.percent = this.calced.max ? this.calced.score * 100 / this.calced.max : 0
        // const percent = max - min ? score * 100 / (max - min) : 0
        this.calced.label = `${$.toFixed(this.calced.wscore)} / ${$.toFixed(this.calced.wmax)}`
        this.calced.color = $.percentToColor(this.calced.percent, 0.5)
        this.calced.progressColor = $.percentToColor(this.calced.percent)
        this.calced.hint = !_.isNaN(this.calced.percent) ? `${$.percentToGrade(this.calced.percent)} (${Math.round(this.calced.percent)}%)` : ''
        this.calced.labelPercent = !_.isNaN(this.calced.percent) ? `${Math.round(this.calced.percent)}%` : '0'
    }
}
